import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpRight, Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";

export const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [projectType, setProjectType] = useState("residential");
  const [county, setCounty] = useState("ontario");

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData(form);
    setSubmitting(true);
    try {
      await createLead({
        source: "contact",
        name: fd.get("name"),
        company: fd.get("company"),
        email: fd.get("email"),
        phone: fd.get("phone") || null,
        project_type: projectType,
        county,
        message: fd.get("message"),
      });
      toast.success("Bid request received. Our team will reach out within one business day.");
      form.reset();
      setProjectType("residential");
      setCounty("ontario");
    } catch (err) {
      toast.error("Couldn't send your request. Please try again or call (714) 637-4405.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-40 bg-ink text-paper grain relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-[0.06] pointer-events-none" />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-primary-glow">Direct line</span>
            <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.05] tracking-[-0.02em] text-paper">
              Talk to <em className="font-light text-paper/75">RCCD directly.</em>
            </h2>
            <p className="mt-5 text-paper/70 text-base max-w-md leading-relaxed">
              No marketing handlers in between — your inquiry goes straight to the team.
            </p>

            <div className="mt-10 space-y-5 max-w-md">
              {[
                { icon: Phone, label: 'Call', value: '(714) 637-4405', href: 'tel:7146374405' },
                { icon: Mail, label: 'Email', value: 'contact@rccdinc.com', href: 'mailto:contact@rccdinc.com' },
                { icon: MapPin, label: 'Service area', value: 'All of Southern California', href: '#coverage' },
                { icon: Clock, label: 'Hours', value: 'Mon — Fri · 8AM to 5PM', href: '#' },
              ].map(item => (
                <a key={item.label} href={item.href} className="flex items-center gap-5 border-t border-paper/15 pt-5 group">
                  <item.icon className="h-4 w-4 text-primary-glow" />
                  <div className="flex-1">
                    <div className="mono text-[10px] uppercase tracking-[0.22em] text-paper/50">{item.label}</div>
                    <div className="font-display text-xl text-paper group-hover:text-primary-glow transition-colors">{item.value}</div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-paper/40 group-hover:text-paper transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="bg-paper text-ink p-8 lg:p-12 grid grid-cols-2 gap-5">
              <div className="col-span-2 flex items-center justify-between border-b border-border pb-4 mb-2">
                <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">RCCD-FORM · Project Inquiry</span>
                <span className="mono text-[10px] uppercase tracking-[0.22em] text-clay">Confidential</span>
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Full name</Label>
                <Input data-testid="contact-name" name="name" required placeholder="Jane Doe" className="mt-2 h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-clay placeholder:text-foreground/30" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Company</Label>
                <Input data-testid="contact-company" name="company" required placeholder="Coastline Communities" className="mt-2 h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-clay placeholder:text-foreground/30" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Email</Label>
                <Input data-testid="contact-email" name="email" required type="email" placeholder="jane@example.com" className="mt-2 h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-clay placeholder:text-foreground/30" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Phone</Label>
                <Input data-testid="contact-phone" name="phone" placeholder="(555) 123 4567" className="mt-2 h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-clay placeholder:text-foreground/30" />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Project type</Label>
                <Select value={projectType} onValueChange={setProjectType}>
                  <SelectTrigger data-testid="contact-project-type" className="mt-2 h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus:ring-0 focus:border-clay">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="mixed">Mixed-use</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Jurisdiction</Label>
                <Select value={county} onValueChange={setCounty}>
                  <SelectTrigger data-testid="contact-county" className="mt-2 h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus:ring-0 focus:border-clay">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ontario">City of Ontario</SelectItem>
                    <SelectItem value="chino">City of Chino</SelectItem>
                    <SelectItem value="sb">San Bernardino Co.</SelectItem>
                    <SelectItem value="rv">Riverside Co.</SelectItem>
                    <SelectItem value="other">Other / Southern California</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Project brief</Label>
                <Textarea data-testid="contact-message" name="message" required rows={4} placeholder="APN, acreage, current zoning, target start, anything else." className="mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg focus-visible:ring-0 focus-visible:border-clay resize-none placeholder:text-foreground/30" />
              </div>

              <div className="col-span-2 mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground max-w-xs">
                  By submitting you agree to be contacted by RCCD. We never share your data.
                </p>
                <Button data-testid="contact-submit" type="submit" disabled={submitting} className="h-14 rounded-none bg-ink text-paper hover:bg-clay mono text-[11px] uppercase tracking-[0.22em] px-8">
                  {submitting ? 'Sending…' : 'Send Request'}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
